import { cn } from '@/platform/lib/utils';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/20/solid';

const variants = {
    info: {
        Icon: InformationCircleIcon,
        container: 'bg-blue-50 outline-blue-200',
        icon: 'text-blue-400',
        title: 'text-blue-800',
        content: 'text-blue-700',
    },
    success: {
        Icon: CheckCircleIcon,
        container: 'bg-green-50 outline-green-200',
        icon: 'text-green-400',
        title: 'text-green-800',
        content: 'text-green-700',
    },
    warning: {
        Icon: ExclamationTriangleIcon,
        container: 'bg-yellow-50 outline-yellow-200',
        icon: 'text-yellow-400',
        title: 'text-yellow-800',
        content: 'text-yellow-700',
    },
    error: {
        Icon: XCircleIcon,
        container: 'bg-red-50 outline-red-200',
        icon: 'text-red-400',
        title: 'text-red-800',
        content: 'text-red-700',
    },
    neutral: {
        Icon: InformationCircleIcon,
        container: 'bg-gray-50 outline-gray-200',
        icon: 'text-gray-400',
        title: 'text-gray-900',
        content: 'text-gray-600',
    },
};

// `icon` overrides the variant default: pass a component to swap it, or false to
// render the message without one.
const Message = ({
    variant = 'info',
    title,
    icon,
    content,
    className,
    children,
    ...props
}) => {
    const styles = variants[variant] ?? variants.info;
    const Icon = icon === undefined ? styles.Icon : icon;
    const body = content ?? children;

    return (
        <div
            className={cn(
                'flex gap-3 rounded-md p-4 outline-1 -outline-offset-1',
                styles.container,
                className
            )}
            {...props}
        >
            {Icon && (
                <Icon
                    aria-hidden='true'
                    className={cn('mt-0.5 size-5 shrink-0', styles.icon)}
                />
            )}
            <div className='min-w-0 flex-1'>
                {title && (
                    <p className={cn('text-sm font-semibold', styles.title)}>
                        {title}
                    </p>
                )}
                {body && (
                    <div
                        className={cn(
                            'text-sm',
                            styles.content,
                            title && 'mt-1'
                        )}
                    >
                        {body}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Message;
